import {Button, Flex, Input, Modal, Select, Switch, Table, TableColumnsType} from 'antd'
import {casinosConfig} from "../components/api";
import {useEffect, useState} from "react";
import {ErrorView} from "../components/show-error";
import StringSelector from "../components/string-selector";
import {useSearchParams} from "react-router";
import {
  Action, CasinoModel,
  MergeModelResponse_Delta,
  Result,
  SettingType,
  SettingTypeSchema
} from '../api/ga/config_manager/casinosconfig/casinos_config_pb'
import {DataEntryParent} from "../components/data-entry-parent";
import ResultModal from "../components/result-modal";

export default function CasinoModelView() {
  const [searchParams, setSearchParams] = useSearchParams()

  const [modal, contextHolder] = Modal.useModal()

  const [rows, setRows] = useState([] as DataType[])
  const [masterRows, setMasterRows] = useState(new Map<string, DataType>())
  const [branches, setBranches] = useState([] as string[])
  const [branch, setBranch] = useState('')
  const [modalOpen, setModalOpen] = useState(false)
  const [modalIsEdit, setModalIsEdit] = useState(true)
  const [modalRow, setModalRow] = useState<DataType | null>(null)
  const [modalRowEdit, setModalRowEdit] = useState<DataType | null>(null)
  const [newBranchOpen, setNewBranchOpen] = useState(false)
  const [newBranchName, setNewBranchName] = useState('')

  const [mergeOpen, setMergeOpen] = useState(false)
  const [mergeDeltas, setMergeDeltas] = useState([] as MergeModelResponse_Delta[])
  const [mergeParams, setMergeParams] = useState({ fromBranch: '', toBranch: '' })

  const [importOpen, setImportOpen] = useState(false)
  const [importRes, setImportRes] = useState<Result | undefined>(undefined)
  const [importAskOpen, setImportAskOpen] = useState(false)
  const [importFromGitBranch, setImportFromGitBranch] = useState('master')
  const [importLoading, setImportLoading] = useState(false)

  const [exportOpen, setExportOpen] = useState(false)
  const [exportRes, setExportRes] = useState<Result | undefined>(undefined)
  const [exportLoading, setExportLoading] = useState(false)

  interface DataType {
    key: string
    branch: string
    setting: string
    settingType: SettingType
    comment: string
    required: boolean
    regexp: string
    action: Action
    children?: DataType[]
  }

  const loadBranches = () => {
    casinosConfig.modelsBranches({})
      .then((resp) => {
        setBranches(resp.branches.sort())
      })
      .catch((e) => {
        ErrorView(modal, 'settingsBranches error', e)
      })
  }

  const loadModels = () => {
    casinosConfig.models({branch:branch})
      .then((resp) => {
        // setting (incl. nested via dots) -> node
        const nodeMap = new Map<string, DataType>()

        function getOrCreate(model: CasinoModel): DataType {
          let node = nodeMap.get(model.setting)
          if (!node) {
            node = {
              key: model.setting,
              branch: model.branch,
              setting: model.setting,
              settingType: model.settingType,
              comment: model.comment,
              required: model.required,
              action: model.action,
              regexp: model.regexp,
              children: undefined,
            }
            nodeMap.set(model.setting, node)
            const lastDot = model.setting.lastIndexOf('.')
            if (lastDot !== -1) {
              const parent = getOrCreate({
                ...model,
                setting: model.setting.substring(0, lastDot),
                settingType: SettingType.UNKNOWN,
                comment: '',
                required: false,
                regexp: '',
                action: Action.NO_OR_UPSET,
              })
              parent.children = [...(parent.children ?? []), node]
            }
          }
          return node
        }

        const masterRowsMap = new Map<string, DataType>()
        resp.models?.forEach((model) => {
          const node = getOrCreate(model)
          node.branch = model.branch
          node.settingType = model.settingType
          node.comment = model.comment
          node.required = model.required
          node.regexp = model.regexp
          node.action = model.action
          if (model.branch === 'master') {
            masterRowsMap.set(model.setting, node)
          } else {
            // propagate the non-master branch up to parent nodes so they render as changed
            let parentSetting = model.setting
            let lastDot = parentSetting.lastIndexOf('.')
            while (lastDot !== -1) {
              parentSetting = parentSetting.substring(0, lastDot)
              const parent = nodeMap.get(parentSetting)
              if (parent && parent.branch === 'master') parent.branch = model.branch
              lastDot = parentSetting.lastIndexOf('.')
            }
          }
        })

        const sortBySetting = (a: DataType, b: DataType) => a.setting.localeCompare(b.setting)

        const roots: DataType[] = []
        nodeMap.forEach((node) => {
          if (node.children) node.children.sort(sortBySetting)
          if (node.setting.lastIndexOf('.') === -1) roots.push(node)
        })
        roots.sort(sortBySetting)

        setRows(roots)
        setMasterRows(masterRowsMap)
      })
      .catch((e) => {
        ErrorView(modal, 'models error', e)
      })
  }

  useEffect(() => {
    setBranch(searchParams.get('branch') ?? 'master')
    loadBranches()
  }, [])

  useEffect(() => {
    if (!branch) return

    setSearchParams((par) => {
      par.set('branch', branch)
      return par
    })

    loadModels()
  }, [branch])

  const selectOptionsSettingTypeSchema: any[] | undefined = []
  SettingTypeSchema.values.forEach((val)=> {
    if (val.number == 0) return

    selectOptionsSettingTypeSchema.push({
      value: val.number,
      label: val.name.toLowerCase(),
    })
  })

  const columns: TableColumnsType<DataType> = [
    {
      title: 'setting',
      dataIndex: 'setting',
      key: 'setting',
      width: '40vw',
      render: (_, { setting }) => {
        const lastDot = setting.lastIndexOf('.')
        return lastDot !== -1 ? setting.substring(lastDot + 1) : setting
      },
    },
    {
      title: 'branch',
      dataIndex: 'branch',
      key: 'branch',
      width: '20vw',
    },
    {
      title: 'type',
      dataIndex: 'settingType',
      key: 'settingType',
      width: '10vw',
      render: (_, { settingType, children }) => children?.length
        ? ''
        : SettingTypeSchema.values.find((v) => v.number === settingType)?.name.toLowerCase() ?? settingType,
    },
    {
      title: 'required',
      dataIndex: 'required',
      key: 'required',
      width: '7vw',
      render: (_, { required, children }) => children?.length ? '' : (required ? '✓' : ''),
    },
    {
      title: 'comment',
      dataIndex: 'comment',
      key: 'comment',
      width: '60vw',
      render: (_, { comment, children }) => children?.length ? '' : comment,
    },
    {
      title: 'regexp',
      dataIndex: 'regexp',
      key: 'regexp',
      width: '40vw',
      render: (_, { regexp, children }) => children?.length ? '' : regexp,
    },
    {
      title: 'actions',
      key: 'actions',
      width: '12vw',
      render: (_, row) => row.children?.length || branch === 'master' ? null : (
        <Flex gap={4}>
          <Button size="small" onClick={() => { setModalIsEdit(true); setModalRow(row); setModalRowEdit({ ...row }); setModalOpen(true) }}>edit</Button>
          <Button size="small" onClick={() => { setModalIsEdit(false); setModalRow(row); setModalRowEdit({ ...row }); setModalOpen(true) }}>copy</Button>
          <Button size="small" danger onClick={() => {
            modal.confirm({
              title: 'Delete setting',
              content: `Delete "${row.setting}"?`,
              onOk: () => {
                if (row.branch === 'master') {
                  casinosConfig.upsertModel({
                    models: [{
                      branch: branch,
                      setting: row.setting,
                      settingType: row.settingType,
                      comment: row.comment,
                      required: row.required,
                      regexp: row.regexp,
                      action: Action.DELETE,
                    }],
                  }).then(() => { setModalOpen(false); loadModels() })
                    .catch((e) => {ErrorView(modal, 'upsertModel error', e)})
                } else {
                  casinosConfig.deleteModel({
                    branch: branch,
                    setting: [row.setting],
                  })
                    .then(() => loadModels())
                    .catch((e) => {
                      ErrorView(modal, 'deleteModel error', e)
                    })
                }
              },
            })
          }}>delete</Button>
        </Flex>
      ),
    },
  ]

  return (
    <>
      <div style={{maxWidth: 1400, margin: '0 auto', padding: '24px 24px 40px', textAlign: 'left'}}>
      <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 0}}>
        <h1 style={{fontSize: 32, fontWeight: 900, color: 'var(--type-primary)', margin: 0}}>
          Casino Model
        </h1>
      </div>
      <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: 16, marginBottom: 16, alignItems: 'start'}}>
        <DataEntryParent text="branch">
          <StringSelector
            items={branches}
            value={[branch]}
            onChange={(value:string[]) => {
              const val = value ? value[0] : ''
              setBranch(val)
            }}
          ></StringSelector>
        </DataEntryParent>
        <DataEntryParent text="New">
          <Button onClick={() => {
            setNewBranchName('');
            setNewBranchOpen(true);
          }}>New Branch from current</Button>
        </DataEntryParent>
        {branch && branch !== 'master' && (
          <>
            <DataEntryParent text="Delete">
              <Button danger onClick={() => {
                modal.confirm({
                  title: 'Delete branch',
                  content: `Delete branch "${branch}" and all its settings?`,
                  onOk: () => {
                    casinosConfig.deleteModel({
                      branch: branch,
                    })
                      .then(() => { setBranch('master'); loadBranches() })
                      .catch((e: any) => { ErrorView(modal, 'deleteModel error', e) })
                  },
                })
              }}>Delete branch</Button>
            </DataEntryParent>
            <DataEntryParent text="Import">
              <Button onClick={() => {
                setImportFromGitBranch('master');
                setImportAskOpen(true)
              }}>Import from git</Button>
            </DataEntryParent>
            <DataEntryParent text="Merge">
              <Button danger onClick={() => {
                const params = { fromBranch: branch, toBranch: 'master' }
                casinosConfig.mergeModelCompare(params)
                  .then((resp) => { setMergeParams(params); setMergeDeltas(resp.deltas ?? []); setMergeOpen(true) })
                  .catch((e: any) => {
                    if (String(e?.message ?? e).includes('no data for merge')) {
                      modal.warning({title: 'Merge', content: 'No data for merge'})
                    } else {
                      ErrorView(modal, 'mergeModelCompare error', e)
                    }
                  })
              }}>Merge branch</Button>
            </DataEntryParent>
          </>
        )}
        <DataEntryParent text="Export">
          <Button loading={exportLoading} onClick={() => {
            setExportLoading(true);
            casinosConfig.exportModels({ fromBranch: branch })
              .then((resp) => {
                setExportRes(resp.res)
                setExportOpen(true)
                if (resp.gitBranch || resp.gitMrLink) {
                  modal.info({
                    title: 'Export pushed to git',
                    content: (
                      <p>
                        Branch <b>{resp.gitBranch}</b> created.{' '}
                        {resp.gitMrLink && <a href={resp.gitMrLink} target="_blank" rel="noreferrer">Open merge request</a>}
                      </p>
                    ),
                  })
                }
              })
              .catch((e: any) => { ErrorView(modal, 'exportModels error', e) })
              .finally(() => setExportLoading(false))
          }}>Export to git</Button>
        </DataEntryParent>
      </div>
      <div style={{background: 'var(--surface-white)', borderRadius: 'var(--radius-basic)', border: '1px solid var(--border)', overflow: 'hidden'}}>
      <Table
        // title={() => 'Casino Model configs'}
        dataSource={rows}
        columns={columns}
        size={'small'}
        pagination={{ defaultPageSize: 100 }}
        rowClassName={(row) => row.branch === 'master'? '' :
          row.action === Action.DELETE ? 'merge-removed' :
            row.children ? 'merge-changed' :
              (masterRows.has(row.key) ? 'merge-changed' : 'merge-added')
        }
      />
      </div>
      </div>
      <Modal
        title={modalIsEdit ? 'Edit' : 'Copy'}
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        onOk={() => {
          if (!modalRowEdit || !modalRow) return

          const doUpsert = () => {
            casinosConfig.upsertModel({
              models: [{
                branch: branch,
                setting: modalRowEdit.setting,
                settingType: modalRowEdit.settingType,
                comment: modalRowEdit.comment,
                required: modalRowEdit.required,
                regexp: modalRowEdit.regexp,
              }],
            }).then(() => { setModalOpen(false); loadModels() })
              .catch((e) => {ErrorView(modal, 'upsertModel error', e)})
          }

          if (!modalIsEdit && modalRowEdit.setting === modalRow.setting) {
            modal.warning({
              title: 'Setting not changed',
              content: 'Please change the setting name before copying.',
            })
            return
          }

          if (modalIsEdit && modalRow && modalRowEdit.setting !== modalRow.setting) {
            modal.confirm({
              title: 'Setting renamed',
              content: `"${modalRow.setting}" → "${modalRowEdit.setting}". Old setting will be deleted. Continue?`,
              onOk: () => {
                if (branch === 'master') {
                  casinosConfig.upsertModel({
                    models: [{
                      branch: branch,
                      setting: modalRowEdit.setting,
                      settingType: modalRowEdit.settingType,
                      comment: modalRowEdit.comment,
                      required: modalRowEdit.required,
                      regexp: modalRowEdit.regexp,
                      action: Action.DELETE,
                    }],
                  }).then(() => { setModalOpen(false); loadModels() })
                    .catch((e) => {ErrorView(modal, 'upsertModel error', e)})
                } else {
                  casinosConfig.deleteModel({
                    branch: branch,
                    setting: [modalRow.setting],
                  })
                    .then(doUpsert)
                    .catch((e) => {
                      ErrorView(modal, 'deleteModel error', e)
                    })
                }
              },
            })
          } else {
            doUpsert()
          }
        }}
      >
        {modalRowEdit && (
          <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: '8px 12px', alignItems: 'center' }}>
            <span style={{ fontWeight: 500 }}>branch</span>
            <span>{modalRowEdit.branch}</span>

            <span style={{ fontWeight: 500 }}>setting</span>
            <Input value={modalRowEdit.setting} onChange={(e) => setModalRowEdit({ ...modalRowEdit, setting: e.target.value })} />

            <span style={{ fontWeight: 500 }}>type</span>
            <Select
              value={modalRowEdit.settingType}
              options={selectOptionsSettingTypeSchema}
              onChange={(val) => setModalRowEdit({ ...modalRowEdit, settingType: val })}
              style={{ width: '100%' }}
            />

            <span style={{ fontWeight: 500 }}>required</span>
            <Switch
              checked={modalRowEdit.required}
              onChange={(val) => setModalRowEdit({ ...modalRowEdit, required: val })}
            />

            <span style={{ fontWeight: 500 }}>comment</span>
            <Input.TextArea
              value={modalRowEdit.comment}
              autoSize={{ minRows: 1, maxRows: 4 }}
              onChange={(e) => setModalRowEdit({ ...modalRowEdit, comment: e.target.value })}
            />

            <span style={{ fontWeight: 500 }}>regexp</span>
            <Input
              value={modalRowEdit.regexp}
              onChange={(e) => setModalRowEdit({ ...modalRowEdit, regexp: e.target.value })}
            />
          </div>
        )}
      </Modal>
      <Modal
        title="New Branch from current"
        open={newBranchOpen}
        onCancel={() => setNewBranchOpen(false)}
        onOk={() => {
          const newBranch = newBranchName.trim()

          if (!newBranch) {
            modal.warning({ title: 'Branch name is empty' })
            return
          }
          if (branches.includes(newBranch)) {
            modal.warning({ title: 'Branch already exists', content: `"${newBranch}" already exists.` })
            return
          }

          casinosConfig.copyModelsBranch({ fromBranch: branch, toBranch: newBranch })
            .then(() => { setNewBranchOpen(false); loadModels(); setBranch(newBranch) })
            .catch((e: any) => { ErrorView(modal, 'copyModelsBranch error', e) })
        }}
      >
        <Input
          placeholder="New branch name"
          value={newBranchName}
          onChange={(e) => setNewBranchName(e.target.value)}
        />
      </Modal>
      <Modal
        title="Merge branch comparison"
        open={mergeOpen}
        onCancel={() => setMergeOpen(false)}
        onOk={() => {
          modal.confirm({
            title: 'Merge branch',
            content: `Merge "${mergeParams.fromBranch}" into "${mergeParams.toBranch}"?`,
            onOk: () => {
              casinosConfig.mergeModelDo(mergeParams)
                .then(() => { setMergeOpen(false); setBranch('master'); loadModels() })
                .catch((e: any) => { ErrorView(modal, 'mergeModelDo error', e) })
            },
          })
        }}
        okText="Merge"
        width="90vw"
      >
        <Table
          size="small"
          pagination={{ pageSize: 25 }}
          rowKey={(_, i) => String(i)}
          rowClassName={(d) => !d.valueTo ? 'merge-added' : !d.valueFrom ? 'merge-removed' : 'merge-changed'}
          dataSource={mergeDeltas}
          columns={[
            { title: 'setting', dataIndex: 'setting', key: 'setting' },
            { title: 'before', dataIndex: 'valueTo', key: 'valueTo', render: (v) => v || <span style={{ color: '#999' }}>&lt;default&gt;</span> },
            { title: 'new', dataIndex: 'valueFrom', key: 'valueFrom', render: (v) => v || <span style={{ color: '#999' }}>&lt;default&gt;</span> },
          ]}
        />
      </Modal>
      <Modal
        title="Import from git"
        open={importAskOpen}
        onCancel={() => setImportAskOpen(false)}
        confirmLoading={importLoading}
        cancelButtonProps={{disabled: importLoading}}
        maskClosable={!importLoading}
        closable={!importLoading}
        onOk={() => {
          const fromGitBranch = importFromGitBranch.trim() || 'master'
          setImportLoading(true);
          casinosConfig.importModels({fromGitBranch: fromGitBranch, toBranch: branch})
            .then((resp) => {
              setImportAskOpen(false);
              setImportRes(resp.res);
              setImportOpen(true)
              loadModels();
            })
            .catch((e: any) => {
              ErrorView(modal, 'importModels error', e)
            })
            .finally(() => setImportLoading(false))
        }}
        okText="Import"
      >
        <div style={{display: 'grid', gridTemplateColumns: '120px 1fr', gap: '8px 12px', alignItems: 'center'}}>
          <span style={{fontWeight: 500}}>from git branch</span>
          <Input
            placeholder="master"
            value={importFromGitBranch}
            onChange={(e) => setImportFromGitBranch(e.target.value)}
          />
          <span style={{fontWeight: 500}}>to branch</span>
          <span>{branch}</span>
        </div>
      </Modal>
      <ResultModal
        title={`Import from git — ${branch}`}
        open={importOpen}
        res={importRes}
        onClose={() => {
          setImportOpen(false);
          loadModels()
        }}
      />
      <ResultModal
        title={`Export to git — ${branch}`}
        open={exportOpen}
        res={exportRes}
        onClose={() => setExportOpen(false)}
      />
      {contextHolder}
    </>
  )
}
